import React, { useState } from 'react';
import { KeyRound, Lock, Eye, EyeOff, X, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useAppContext } from '../context/AppContext';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ChangePasswordModal({ isOpen, onClose }: ChangePasswordModalProps) {
  const { currentUser } = useAppContext();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!isOpen || !currentUser) return null;

  const handleReset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(false);
    setLoading(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!currentPassword) {
      setError('Lütfen mevcut şifrenizi giriniz.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Yeni şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Yeni şifreler eşleşmiyor. Lütfen kontrol ediniz.');
      return;
    }
    if (currentPassword === newPassword) {
      setError('Yeni şifreniz mevcut şifrenizle aynı olamaz.');
      return;
    }

    setLoading(true);

    try {
      if (auth.currentUser && auth.currentUser.email) {
        // Re-authenticate user first for security
        const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
        await reauthenticateWithCredential(auth.currentUser, credential);
        await updatePassword(auth.currentUser, newPassword);
        
        setSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 1800);
      } else {
        throw new Error('Kullanıcı oturumu bulunamadı.');
      }
    } catch (err: any) {
      console.error('Password change error:', err);
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Mevcut şifreniz hatalı. Lütfen tekrar deneyiniz.');
      } else if (err.code === 'auth/weak-password') {
        setError('Yeni şifre çok zayıf. Daha güçlü bir şifre seçiniz.');
      } else if (err.code === 'auth/requires-recent-login') {
        setError('Güvenlik nedeniyle şifre değiştirmek için tekrar giriş yapmalısınız.');
      } else {
        setError('Şifre güncellenirken bir hata oluştu: ' + (err.message || 'Lütfen tekrar deneyiniz.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-xs animate-in fade-in duration-150 no-print">
      <div className="bg-white rounded-2xl shadow-2xl border border-stone-200 w-full max-w-md overflow-hidden transform transition-all">
        
        {/* Header */}
        <div className="bg-stone-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2.5">
            <div className="p-2 bg-[#945f46] text-white rounded-xl shadow-xs">
              <KeyRound className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-bold text-sm leading-none">Şifre Değiştir</h3>
              <p className="text-[11px] text-stone-400 mt-1">Hesap şifrenizi güvenle güncelleyin</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-stone-400 hover:text-white p-1 rounded-lg hover:bg-stone-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          
          {/* Success Banner */}
          {success && (
            <div className="p-3.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl flex items-center space-x-2.5 text-xs font-semibold animate-in fade-in">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Şifreniz başarıyla güncellendi! Kapatılıyor...</span>
            </div>
          )}

          {/* Error Banner */}
          {error && (
            <div className="p-3.5 bg-red-50 border border-red-200 text-red-800 rounded-xl flex items-center space-x-2.5 text-xs font-medium animate-in fade-in">
              <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Mevcut Şifre */}
          <div>
            <label className="block text-[11px] font-bold uppercase text-stone-600 mb-1">
              Mevcut Şifre
            </label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                disabled={loading || success}
                className="w-full pl-9 pr-10 py-2.5 text-xs text-stone-900 bg-white placeholder-stone-400 font-medium rounded-xl border border-stone-200 focus:outline-none focus:border-[#945f46] focus:ring-1 focus:ring-[#945f46]"
              />
              <Lock className="w-3.5 h-3.5 text-stone-400 absolute left-3 top-2.5" />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-2.5 text-stone-400 hover:text-stone-600"
              >
                {showCurrent ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          {/* Yeni Şifre */}
          <div>
            <label className="block text-[11px] font-bold uppercase text-stone-600 mb-1">
              Yeni Şifre
            </label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="En az 6 karakter"
                disabled={loading || success}
                className="w-full pl-9 pr-10 py-2.5 text-xs text-stone-900 bg-white placeholder-stone-400 font-medium rounded-xl border border-stone-200 focus:outline-none focus:border-[#945f46] focus:ring-1 focus:ring-[#945f46]"
              />
              <Lock className="w-3.5 h-3.5 text-stone-400 absolute left-3 top-2.5" />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-2.5 text-stone-400 hover:text-stone-600"
              >
                {showNew ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          {/* Yeni Şifre Tekrar */}
          <div>
            <label className="block text-[11px] font-bold uppercase text-stone-600 mb-1">
              Yeni Şifre (Tekrar)
            </label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Şifreyi tekrar giriniz"
                disabled={loading || success}
                className="w-full pl-9 pr-10 py-2.5 text-xs text-stone-900 bg-white placeholder-stone-400 font-medium rounded-xl border border-stone-200 focus:outline-none focus:border-[#945f46] focus:ring-1 focus:ring-[#945f46]"
              />
              <Lock className="w-3.5 h-3.5 text-stone-400 absolute left-3 top-2.5" />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-2.5 text-stone-400 hover:text-stone-600"
              >
                {showConfirm ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-2 pt-3 border-t border-stone-100">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-xs font-bold text-stone-600 hover:text-stone-900 bg-stone-100 hover:bg-stone-200 rounded-xl transition-colors"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={loading || success}
              className="px-5 py-2 text-xs font-bold text-white bg-[#945f46] hover:bg-[#83523c] rounded-xl shadow-md transition-all flex items-center space-x-1.5 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Güncelleniyor...</span>
                </>
              ) : (
                <span>Şifreyi Güncelle</span>
              )}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}
